import React, { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/material/styles";

import AttachFileIcon from "@mui/icons-material/AttachFile";

import FileOutlined from "../../assets/FileOutlined.svg";
import TrashOutlined from "../../assets/TrashIconOutlined.svg";

import { LoansContext } from "../../context/LoansContext";
import { SUPPORTED_DOCUMENT_ACCEPT, partitionBySupportedFileType } from "../../utils/fileValidation";

import MitigationFactorSelect from "./MitigationFactorSelect";
import {
  MIN_MITIGATION_FACTORS,
  GENERAL_WAIVER_MITIGATION_FACTOR_OPTIONS,
  asMitigationFactorArray,
  persistNewWaiver,
  uploadWaiverDocuments,
  WaiverServiceRecord,
} from "./utils";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Loan-level General Waiver request (TA-11746) — not tied to any specific
// failed test, so there's no takeoutPartner/testTitle. Otherwise mirrors
// WaiverRequestForm.tsx: same min-two-factors rule, same "Other requires
// free text" behavior, same drag-and-drop multi-file upload, same supported-
// document-type restriction — but with its own mitigation factor list.
export default function GeneralWaiverRequestForm({
  onSubmit,
  onCancel,
}: {
  onSubmit?: (data: WaiverServiceRecord) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const { setLoanDetails, assignedPartner } = useContext(LoansContext) ?? {};
  const { loanId } = useParams<{ loanId?: string }>();

  const [mitigationFactor, setMitigationFactor] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const isOtherSelected = mitigationFactor.includes("Other");
  // Files are only staged locally until Submit — the waiver-service upload
  // endpoints are scoped to a waiverId, which doesn't exist until the waiver
  // itself has been created, so nothing is uploaded anywhere yet at this
  // point (removing a staged file is a pure local no-op).
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [fileError, setFileError] = useState("");

  // accept only filters the native file-picker dialog — drag-and-drop
  // bypasses it entirely, so unsupported files are also rejected here.
  const addFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    const { supported, unsupported } = partitionBySupportedFileType(files);
    setFileError(unsupported.length ? t("unsupportedFileType") : "");
    if (supported.length) setPendingFiles((prev) => [...prev, ...supported]);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files ?? []);
    event.target.value = "";
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    if (event.dataTransfer.files) addFiles(event.dataTransfer.files);
  };

  const handleDeleteDocument = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, docIdx) => docIdx !== index));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const record = await persistNewWaiver(
        loanId,
        setLoanDetails,
        {
          description,
          mitigationFactor: asMitigationFactorArray(mitigationFactor),
          isGeneralWaiver: true,
        },
        assignedPartner,
      );
      const finalRecord = await uploadWaiverDocuments(setLoanDetails, record, pendingFiles);
      onSubmit?.(finalRecord);
    } catch (err) {
      setSubmitError(t("waiverSubmitFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      data-testid="general-waiver-request-form"
      sx={{
        marginTop: "24px",
        borderRadius: "6px",
        border: "1px solid #B8B8C7",
        background: "#fff",
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            padding: "10px 16px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
            alignSelf: "stretch",
            borderRadius: "4px 4px 0 0",
            border: "1px solid #DBE2FF",
            background: "#F8F9FD",
            width: "auto",
          }}
        >
          <h3
            style={{
              color: "#000",
              fontFamily: "Poppins",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
              margin: 0,
              padding: 0,
            }}
          >
            {t("requestingGeneralWaiver")}
          </h3>
        </Grid>
      </Grid>
      <Grid container style={{ padding: "16px" }}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <label
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 300,
              }}
              htmlFor="general-mitigation-factor-label"
            >
              {t("mitigationFactor")} <sup style={{ color: "red" }}>*</sup>
            </label>
            <MitigationFactorSelect
              value={mitigationFactor}
              onChange={(selected) => {
                setMitigationFactor(selected);
                if (!selected.includes("Other")) {
                  setDescription("");
                }
              }}
              labelId="general-mitigation-factor-label"
              testId="general-mitigation-factor-select"
              options={GENERAL_WAIVER_MITIGATION_FACTOR_OPTIONS}
            />
          </FormControl>
        </Grid>

        {isOtherSelected ? (
          <Grid item xs={12} style={{ paddingTop: "16px" }}>
            <span
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 300,
              }}
            >
              {t("waiverDescription")}
              <sup style={{ color: "red" }}>*</sup>
            </span>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              data-testid="general-waiver-description-input"
              style={{ background: "#F8F9FD" }}
            />
          </Grid>
        ) : null}

        <Grid item xs={12} style={{ paddingTop: "16px" }}>
          <span
            style={{
              fontSize: "12px",
              lineHeight: "20px",
              letterSpacing: "0.4px",
              fontWeight: 300,
            }}
          >
            {t("supportDocument")}
          </span>
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<AttachFileIcon />}
            data-testid="general-waiver-upload-button"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              background: isDragActive ? "#EAEFFF" : "#F8F9FD",
              borderColor: isDragActive ? "#3E5EEC" : undefined,
              borderStyle: "dashed",
            }}
          >
            <span>{t("dragAndDropOrUpload")}</span>
            <VisuallyHiddenInput
              type="file"
              accept={SUPPORTED_DOCUMENT_ACCEPT}
              onChange={handleFileUpload}
              multiple
            />
          </Button>
        </Grid>

        {fileError ? (
          <Grid item xs={12} style={{ paddingTop: "8px" }}>
            <div style={{ color: "#ef4444", fontSize: "12px" }}>{fileError}</div>
          </Grid>
        ) : null}

        {pendingFiles.length > 0 ? (
          <Grid item xs={12} style={{ paddingTop: "16px" }}>
            <span>{t("textUploaded", { count: pendingFiles.length })}</span>
            {pendingFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                data-testid="general-waiver-uploaded-document-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 10px",
                  marginBottom: "6px",
                  borderRadius: "4px",
                  background: "white",
                  border: "1px solid #B8B8C7",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={FileOutlined}
                    alt="file-icon"
                    style={{ width: "16px", height: "16px", marginRight: "8px" }}
                  />
                  <span style={{ fontSize: "14px" }}>{file.name}</span>
                </div>
                <IconButton
                  size="small"
                  aria-label="delete"
                  onClick={() => handleDeleteDocument(index)}
                  data-testid="general-waiver-delete-document-button"
                >
                  <img src={TrashOutlined} alt="delete-icon" style={{ width: "16px", height: "16px" }} />
                </IconButton>
              </div>
            ))}
          </Grid>
        ) : null}

        {submitError ? (
          <Grid item xs={12} style={{ paddingTop: "8px" }}>
            <div style={{ color: "#ef4444", fontSize: "12px" }}>
              {submitError}
            </div>
          </Grid>
        ) : null}

        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingTop: "16px" }}
        >
          <Button
            variant="text"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="general-waiver-cancel-button"
          >
            {t("cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={
              mitigationFactor.length < MIN_MITIGATION_FACTORS ||
              (isOtherSelected && !description.trim())
            }
            data-testid="general-waiver-submit-button"
          >
            {t("submitWaiver")}
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
}
